/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult
} from "react-query";
import { useDispatch } from "react-redux";
import {
  deleteAttendancePunch,
  getAttendancePunches,
  patchAttendancePunch,
  postAttendancePunch
} from "../../api/attendancePunch";
import { IAttendancePunch, IAttendancePunchForm } from "../../interfaces/AttendanceInterface";
import { ApiResponse } from "../../interfaces/Api";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";

const attendancePunchesQueryKey = "attendancePunches";

// get attendance punches
export function useAttendancePunches(params?: {
  employeeIds?: number[];
  serviceProviderIds?: number[];
  fromDate?: string;
  toDate?: string;
  dutyType?: string;
}): UseQueryResult<IAttendancePunch[], Error> {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery(
    [attendancePunchesQueryKey, resourceCentre?.id, params],
    async () => {
      if (!resourceCentre?.id) return [];
      const response = await getAttendancePunches({
        resourceCentreId: resourceCentre.id,
        ...params
      });
      return response.data;
    },
    {
      enabled: !!resourceCentre?.id
    }
  );
}

// post attendance punch
export function usePostAttendancePunch(): UseMutationResult<
  ApiResponse<IAttendancePunch>,
  Error,
  Partial<IAttendancePunchForm>
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((data: Partial<IAttendancePunchForm>) => postAttendancePunch(data), {
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries(attendancePunchesQueryKey);
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Punch recorded successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Failed to record punch",
          variant: "error"
        })
      );
    }
  });
}

// patch attendance punch
export function usePatchAttendancePunch(): UseMutationResult<
  ApiResponse<IAttendancePunch>,
  Error,
  { id: number | string; data: Partial<IAttendancePunchForm> }
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation(
    ({ id, data }: { id: number | string; data: Partial<IAttendancePunchForm> }) =>
      patchAttendancePunch({ id, data }),
    {
      onSuccess: (response) => {
        if (response.success) {
          queryClient.invalidateQueries(attendancePunchesQueryKey);
          dispatch(
            notificationAdd({
              id: Date.now(),
              autoTimeout: true,
              message: "Punch updated successfully",
              variant: "success"
            })
          );
        }
      },
      onError: (error: any) => {
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: error?.message || error?.data?.message || "Failed to update punch",
            variant: "error"
          })
        );
      }
    }
  );
}

// delete attendance punch
export function useDeleteAttendancePunch(): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((id: number | string) => deleteAttendancePunch(id), {
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries(attendancePunchesQueryKey);
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Punch deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Failed to delete punch",
          variant: "error"
        })
      );
    }
  });
}
